import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Save, Phone, MessageSquare, Heart, ClipboardList, Sparkles,
  CalendarPlus, CalendarClock, CheckCircle2, XCircle, Plus, X,
  PhoneCall, Wallet, Zap,
} from "lucide-react";
import { toast } from "sonner";

const EVENT_ICONS = {
  call: Phone,
  sms: MessageSquare,
  comment: ClipboardList,
  wish: Heart,
  recommendation_created: Sparkles,
  recommendation_closed: XCircle,
  recommendation_status_changed: Sparkles,
  operation_scheduled: CalendarPlus,
  operation_rescheduled: CalendarClock,
  operation_completed: CheckCircle2,
  operation_cancelled: XCircle,
};

const EVENT_COLOR = {
  call: "text-sky-700 bg-sky-50",
  sms: "text-violet-700 bg-violet-50",
  comment: "text-slate-700 bg-slate-100",
  wish: "text-pink-700 bg-pink-50",
  recommendation_created: "text-emerald-700 bg-emerald-50",
  recommendation_closed: "text-rose-700 bg-rose-50",
  recommendation_status_changed: "text-amber-700 bg-amber-50",
  operation_scheduled: "text-indigo-700 bg-indigo-50",
  operation_rescheduled: "text-amber-700 bg-amber-50",
  operation_completed: "text-emerald-700 bg-emerald-50",
  operation_cancelled: "text-rose-700 bg-rose-50",
};

const MANUAL_TYPES = [
  { value: "call", label: "Дзвінок" },
  { value: "sms", label: "SMS" },
  { value: "comment", label: "Коментар" },
  { value: "wish", label: "Побажання" },
];

const LIFECYCLE_BADGE = {
  new: "bg-slate-100 text-slate-700",
  thinking: "bg-amber-50 text-amber-700",
  collecting: "bg-orange-50 text-orange-700",
  callback: "bg-sky-50 text-sky-700",
  scheduled: "bg-violet-50 text-violet-700",
  operated: "bg-emerald-50 text-emerald-700",
  refused: "bg-rose-50 text-rose-700",
  irrelevant: "bg-slate-100 text-slate-500",
};

function withUAPrefix(value) {
  if (!value) return "+380";
  let v = value.replace(/[^\d+]/g, "");
  if (!v.startsWith("+380")) {
    const digits = v.replace(/\D/g, "");
    v = "+380" + (digits.startsWith("380") ? digits.slice(3) : digits);
  }
  return v.slice(0, 13);
}

function computeAge(birth_date) {
  if (!birth_date) return "";
  const d = new Date(birth_date);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

function formatDateTime(s) {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("uk-UA", { dateStyle: "medium", timeStyle: "short" });
}

// Iteration 22: reserved marketing source label — verbatim match with backend seed_catalog.py
const EXTERNAL_MARKETING_LABEL = "Направлення зовнішніх лікарів";

const STAGE_OPTIONS = [
  { value: "consultation", label: "Консультація" },
  { value: "recommended", label: "Рекомендована операція" },
  { value: "waiting", label: "Очікує рішення" },
  { value: "scheduled", label: "Записаний на операцію" },
  { value: "operated", label: "Прооперований" },
  { value: "control_1d", label: "Контроль 1 день" },
  { value: "control_14d", label: "Контроль 14 днів" },
  { value: "control_30d", label: "Контроль 30 днів" },
];

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [events, setEvents] = useState([]);
  const [recs, setRecs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [opTypes, setOpTypes] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [sources, setSources] = useState([]);
  const [tab, setTab] = useState("general");
  const [eventDlg, setEventDlg] = useState(false);
  const [eventForm, setEventForm] = useState({ event_type: "comment", title: "", description: "" });
  const [saving, setSaving] = useState(false);

  // Quick action dialogs
  const [quickRecOpen, setQuickRecOpen] = useState(false);
  const [quickRec, setQuickRec] = useState({
    diagnosis: "", procedure_id: "", eye: "",
    patient_type: "primary",
    referral_source: "", external_doctor_id: "",
    manager_id: "", call_date: "", last_comment: "", comment: "",
    business_trip_team: [],
  });
  const [quickCallOpen, setQuickCallOpen] = useState(false);
  const [quickCall, setQuickCall] = useState({ call_date: "", manager_id: "", comment: "" });
  const [quickNoteOpen, setQuickNoteOpen] = useState(false);
  const [quickNote, setQuickNote] = useState({ description: "" });
  const [extDoctors, setExtDoctors] = useState([]);
  const [marketingSources, setMarketingSources] = useState([]);
  // Iteration 30h: gate write actions by patients.write permission
  const { user } = useAuth();
  const canWrite = !!user?.permissions?.includes("patients.write");

  const load = async () => {
    // Iteration 30g: Promise.allSettled so a 403 on any lookup does NOT kill the page.
    // Primary /patients failure is toasted; secondary lookups degrade silently.
    const results = await Promise.allSettled([
      api.get(`/patients/${id}`),
      api.get(`/patients/${id}/events`),
      api.get(`/recommendations?patient_id=${id}`),
      api.get(`/employees`),
      api.get(`/settings/operation-types`),
      api.get(`/catalog/procedures?include_inactive=false`),
      api.get(`/catalog/referral-sources?include_inactive=false`),
      api.get(`/catalog/external-doctors?include_inactive=false`),
      api.get(`/catalog/marketing-sources?include_inactive=false`),
    ]);
    const [p, ev, rc, em, ot, pr, sr, ed, ms] = results;
    if (p.status !== "fulfilled") {
      toast.error(formatApiError(p.reason?.response?.data?.detail) || "Помилка завантаження пацієнта");
      return;
    }
    const pData = p.value.data || {};
    if (!pData.address) {
      const join = [pData.city, pData.street && `вул. ${pData.street}`,
        pData.house && `буд. ${pData.house}`, pData.apartment && `кв. ${pData.apartment}`]
        .filter(Boolean).join(", ");
      if (join) pData.address = join;
    }
    setPatient(pData);
    setEvents(ev.status === "fulfilled" ? (ev.value.data || []) : []);
    setRecs(rc.status === "fulfilled" ? (rc.value.data || []) : []);
    setEmployees(em.status === "fulfilled" ? (em.value.data || []) : []);
    setOpTypes(ot.status === "fulfilled" ? (ot.value.data || []) : []);
    setProcedures(pr.status === "fulfilled" ? (pr.value.data || []) : []);
    setSources(sr.status === "fulfilled" ? (sr.value.data || []) : []);
    setExtDoctors(ed.status === "fulfilled" ? (ed.value.data || []) : []);
    setMarketingSources(ms.status === "fulfilled" ? (ms.value.data || []) : []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const age = useMemo(() => computeAge(patient?.birth_date), [patient]);

  const savePatient = async () => {
    setSaving(true);
    try {
      await api.put(`/patients/${id}`, {
        full_name: patient.full_name,
        birth_date: patient.birth_date,
        phone: patient.phone,
        phone_alt: patient.phone_alt,
        city: "",
        street: "",
        house: "",
        apartment: "",
        address_comment: patient.address_comment,
        address: patient.address || "",
        comment: patient.comment,
        current_stage: patient.current_stage,
        marketing_source: patient.marketing_source || "",
        marketing_external_doctor_id: patient.marketing_external_doctor_id || "",
      });
      toast.success("Збережено");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const addEvent = async () => {
    try {
      await api.post(`/patients/${id}/events`, eventForm);
      setEventDlg(false);
      setEventForm({ event_type: "comment", title: "", description: "" });
      await load();
      toast.success("Подію додано");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const quickSrc = sources.find((s) => s.key === quickRec.referral_source);
  const quickIsReferred = quickRec.patient_type === "referred";
  const quickSrcNeedsTeam = !!quickSrc?.requires_team && quickIsReferred;
  const quickSrcIsExtDoctor = quickSrc?.source_type === "external_doctor" && quickIsReferred;

  const submitQuickRec = async () => {
    if (!quickRec.diagnosis?.trim()) { toast.error("Заповніть діагноз"); return; }
    if (!quickRec.procedure_id) { toast.error("Оберіть процедуру"); return; }
    if (quickIsReferred) {
      if (!quickRec.referral_source) { toast.error("Оберіть джерело направлення"); return; }
      if (quickSrcIsExtDoctor && !quickRec.external_doctor_id) { toast.error("Оберіть зовнішнього лікаря"); return; }
      if (quickSrcNeedsTeam && (quickRec.business_trip_team || []).length === 0) {
        toast.error("Це джерело направлення вимагає вказати бригаду направлення"); return;
      }
    }
    try {
      const payload = {
        patient_id: id,
        diagnosis: quickRec.diagnosis,
        items: [{
          procedure_id: quickRec.procedure_id,
          eye: quickRec.eye || "",
        }],
        patient_type: quickRec.patient_type,
        referral_source: quickIsReferred ? quickRec.referral_source : "",
        external_doctor_id: quickSrcIsExtDoctor ? quickRec.external_doctor_id : "",
        clinic_team: [],
        business_trip_team: quickSrcNeedsTeam ? (quickRec.business_trip_team || []) : [],
        comment: quickRec.comment,
        lifecycle_status: "new",
        manager_id: quickRec.manager_id,
        call_date: quickRec.call_date,
        last_comment: quickRec.last_comment,
      };
      await api.post("/recommendations", payload);
      toast.success("Рекомендацію створено");
      setQuickRecOpen(false);
      setQuickRec({ diagnosis: "", procedure_id: "", eye: "", patient_type: "primary", referral_source: "", external_doctor_id: "", manager_id: "", call_date: "", last_comment: "", comment: "", business_trip_team: [] });
      setTab("recommendations");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitQuickCall = async () => {
    if (!quickCall.call_date) { toast.error("Вкажіть дату дзвінка"); return; }
    try {
      // Update the most recent active (non-closed) recommendation if present
      const target = recs.find((r) => !r.closed && r.status === "active");
      if (target) {
        await api.put(`/recommendations/${target.id}`, {
          patient_id: id,
          operation_type: target.operation_type,
          eye: target.eye,
          patient_type: target.patient_type || "primary",
          referral_source: target.referral_source || "",
          external_doctor_id: target.external_doctor_id || "",
          clinic_team: target.clinic_team || [],
          business_trip_team: target.business_trip_team || [],
          business_trip_comment: target.business_trip_comment || "",
          comment: target.comment || "",
          lifecycle_status: "callback",
          manager_id: quickCall.manager_id || target.manager_id || "",
          call_date: quickCall.call_date,
          last_comment: quickCall.comment || target.last_comment || "",
        });
      }
      // Always also emit a manual "call" event so it appears on the timeline
      const mgr = employees.find((e) => e.id === quickCall.manager_id);
      const desc = [
        mgr && `Менеджер: ${mgr.full_name}`,
        quickCall.comment,
      ].filter(Boolean).join("\n");
      await api.post(`/patients/${id}/events`, {
        event_type: "call",
        title: `Запланований дзвінок на ${quickCall.call_date}`,
        description: desc,
        occurred_at: new Date().toISOString(),
      });
      toast.success(target ? "Дзвінок заплановано (оновлено рекомендацію)" : "Дзвінок заплановано");
      setQuickCallOpen(false);
      setQuickCall({ call_date: "", manager_id: "", comment: "" });
      setTab("events");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitQuickNote = async () => {
    if (!quickNote.description?.trim()) { toast.error("Введіть коментар"); return; }
    try {
      await api.post(`/patients/${id}/events`, {
        event_type: "comment",
        title: "Коментар",
        description: quickNote.description,
      });
      toast.success("Коментар додано");
      setQuickNoteOpen(false);
      setQuickNote({ description: "" });
      setTab("events");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!patient) return <div className="p-8 text-slate-500">Завантаження…</div>;

  return (
    <div data-testid="patient-detail-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft className="h-4 w-4 mr-1" />Назад</Button>
        <div className="text-xs text-slate-500">ID: {patient.chart_no ?? "—"}</div>
      </div>

      <Card className="gv-card">
        <CardContent className="p-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h1 className="heading text-3xl font-bold text-[#0F2027]">{patient.full_name}</h1>
              <div className="text-sm text-slate-500 mt-1">
                {patient.phone || "—"} {age !== "" && <span className="ml-2">· {age} р.</span>}
              </div>
            </div>
            <Badge className="bg-[#0D9488]/10 text-[#0D9488]">{STAGE_OPTIONS.find(s => s.value === (patient.current_stage || "consultation"))?.label || "Консультація"}</Badge>
          </div>
        </CardContent>
      </Card>

      {/* QUICK ACTIONS */}
      <Card className="gv-card" data-testid="quick-actions">
        <CardContent className="p-4 md:p-5">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="h-4 w-4 text-[#0D9488]" />
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Швидкі дії</div>
            {!canWrite && (
              <span className="ml-auto text-xs text-slate-400">режим перегляду</span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Button onClick={() => navigate(`/recommendations?create=1&patient_id=${id}`)} data-testid="qa-create-rec"
                    disabled={!canWrite}
                    className="bg-[#0F2027] hover:bg-[#0F2027]/90 text-white justify-start disabled:opacity-50">
              <Sparkles className="h-4 w-4 mr-2" />Створити рекомендацію
            </Button>
            <Button onClick={() => setQuickCallOpen(true)} data-testid="qa-schedule-call"
                    disabled={!canWrite}
                    className="bg-[#0D9488] hover:bg-[#0D9488]/90 text-white justify-start disabled:opacity-50">
              <PhoneCall className="h-4 w-4 mr-2" />Запланувати дзвінок
            </Button>
            <Button onClick={() => setQuickNoteOpen(true)} data-testid="qa-add-comment"
                    disabled={!canWrite}
                    variant="outline" className="justify-start">
              <ClipboardList className="h-4 w-4 mr-2" />Додати коментар
            </Button>
            <Button disabled data-testid="qa-add-payment"
                    variant="outline" className="justify-start opacity-60"
                    title="Буде доступно пізніше — модуль оплат поки що прив'язаний до операції">
              <Wallet className="h-4 w-4 mr-2" />Додати оплату · скоро
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="general" data-testid="tab-general">Загальна інформація</TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-events">Події ({events.length})</TabsTrigger>
          <TabsTrigger value="recommendations" data-testid="tab-recommendations">Рекомендації ({recs.length})</TabsTrigger>
        </TabsList>

        {/* GENERAL */}
        <TabsContent value="general">
          <Card className="gv-card"><CardContent className="p-6 space-y-6">
            <fieldset disabled={!canWrite} className={!canWrite ? "opacity-90 [&_input]:cursor-not-allowed [&_select]:cursor-not-allowed" : ""}>
            {/* Personal */}
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Особисті дані</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-3">
                  <Label>ПІБ</Label>
                  <Input data-testid="pat-name" value={patient.full_name || ""} onChange={(e) => setPatient({ ...patient, full_name: e.target.value })} />
                </div>
                <div>
                  <Label>Дата народження</Label>
                  <Input type="date" data-testid="pat-birth" value={patient.birth_date || ""} onChange={(e) => setPatient({ ...patient, birth_date: e.target.value })} />
                </div>
                <div>
                  <Label>Вік</Label>
                  <Input readOnly disabled value={age !== "" ? `${age} р.` : ""} className="bg-slate-50" />
                </div>
                <div>
                  <Label>Етап</Label>
                  <Select
                    value={patient.current_stage || "consultation"}
                    onValueChange={async (v) => {
                      try {
                        await api.post(`/funnel/patients/${id}/stage`, { stage: v, note: "" });
                        setPatient({ ...patient, current_stage: v });
                        toast.success("Етап оновлено");
                        await load();
                      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                    }}
                  >
                    <SelectTrigger data-testid="pat-stage-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STAGE_OPTIONS.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Contacts */}
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Контакти</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Основний телефон</Label>
                  <Input data-testid="pat-phone" value={patient.phone || ""} onChange={(e) => setPatient({ ...patient, phone: withUAPrefix(e.target.value) })} placeholder="+380 XX XXX XX XX" />
                </div>
                <div>
                  <Label>Додатковий телефон</Label>
                  <Input data-testid="pat-phone-alt" value={patient.phone_alt || ""} onChange={(e) => setPatient({ ...patient, phone_alt: withUAPrefix(e.target.value) })} placeholder="+380 XX XXX XX XX" />
                </div>
              </div>
            </div>

            {/* Address — single-line */}
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Адреса</div>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label>Адреса</Label>
                  <Input
                    data-testid="pat-address"
                    value={patient.address || ""}
                    onChange={(e) => setPatient({ ...patient, address: e.target.value, city: "", street: "", house: "", apartment: "" })}
                    placeholder="напр. м. Львів, вул. Залізнична 7, кв. 12"
                  />
                </div>
                <div>
                  <Label>Коментар до адреси</Label>
                  <Input data-testid="pat-address-comment" value={patient.address_comment || ""} onChange={(e) => setPatient({ ...patient, address_comment: e.target.value })} placeholder="код домофону, орієнтир тощо" />
                </div>
              </div>
            </div>

            {/* Marketing source */}
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Звідки дізнався (маркетинг)</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-3">
                  <Label>Маркетингове джерело</Label>
                  <Select value={patient.marketing_source || "none"} onValueChange={(v) => setPatient({ ...patient, marketing_source: v === "none" ? "" : v, marketing_external_doctor_id: v === EXTERNAL_MARKETING_LABEL ? patient.marketing_external_doctor_id : "" })}>
                    <SelectTrigger data-testid="pat-source"><SelectValue placeholder="— Не вказано —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Не вказано —</SelectItem>
                      {marketingSources.map((s) => <SelectItem key={s.id} value={s.label}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="text-xs text-slate-500 mt-1">Це поле використовується тільки для маркетингової аналітики. НЕ впливає на нарахування зарплати.</div>
                </div>
                {patient.marketing_source === EXTERNAL_MARKETING_LABEL && (
                  <div className="md:col-span-3">
                    <Label>Зовнішній лікар <span className="text-rose-500">*</span></Label>
                    <Select value={patient.marketing_external_doctor_id || "none"}
                      onValueChange={(v) => setPatient({ ...patient, marketing_external_doctor_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="pat-ext-doctor"><SelectValue placeholder="— Оберіть лікаря —" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Оберіть лікаря —</SelectItem>
                        {extDoctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.full_name}{d.speciality ? ` · ${d.speciality}` : ""}{d.city ? ` · ${d.city}` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="text-xs text-slate-500 mt-1">Ця інформація автоматично буде використана при створенні рекомендації як «Джерело направлення».</div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label>Коментар</Label>
              <Input data-testid="pat-comment" value={patient.comment || ""} onChange={(e) => setPatient({ ...patient, comment: e.target.value })} />
            </div>

            <div className="flex justify-end items-center gap-2">
              {!canWrite && <span className="text-xs text-slate-400">режим перегляду — редагування вимкнено</span>}
              <Button onClick={savePatient} disabled={saving || !canWrite} className="bg-[#0F2027]" data-testid="pat-save"><Save className="h-4 w-4 mr-1" />Зберегти</Button>
            </div>
            </fieldset>
          </CardContent></Card>
        </TabsContent>

        {/* EVENTS */}
        <TabsContent value="events">
          <Card className="gv-card"><CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="font-semibold text-[#0F2027]">Хронологія подій</div>
              <Button size="sm" onClick={() => setEventDlg(true)} disabled={!canWrite} className="bg-[#0D9488] disabled:opacity-50" data-testid="add-event-btn"><Plus className="h-4 w-4 mr-1" />Додати подію</Button>
            </div>
            {events.length === 0 ? (
              <div className="text-sm text-slate-500 py-8 text-center">Поки що подій не зафіксовано.</div>
            ) : (
              <ol className="relative border-l-2 border-slate-200 ml-2 space-y-4">
                {events.map((ev) => {
                  const Icon = EVENT_ICONS[ev.event_type] || ClipboardList;
                  const cls = EVENT_COLOR[ev.event_type] || "text-slate-700 bg-slate-100";
                  return (
                    <li key={ev.id} className="ml-6" data-testid={`event-${ev.id}`}>
                      <span className={`absolute -left-3.5 flex items-center justify-center w-7 h-7 rounded-full ring-4 ring-white ${cls}`}>
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <div className="text-xs text-slate-500">{formatDateTime(ev.occurred_at)} · {ev.event_type_label} {ev.actor_name && `· ${ev.actor_name}`}</div>
                      <div className="font-medium text-[#0F2027]">{ev.title}</div>
                      {ev.description && <div className="text-sm text-slate-600 mt-0.5 whitespace-pre-wrap">{ev.description}</div>}
                    </li>
                  );
                })}
              </ol>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* RECOMMENDATIONS */}
        <TabsContent value="recommendations">
          <Card className="gv-card"><CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="font-semibold text-[#0F2027]">Рекомендації пацієнта</div>
              <Link to="/recommendations" className="text-xs text-[#0D9488] underline">Усі рекомендації</Link>
            </div>
            {recs.length === 0 ? (
              <div className="text-sm text-slate-500 py-8 text-center">Рекомендацій немає.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-slate-500 sticky top-0 z-10 bg-white">
                    <tr><th className="text-left p-2">Створено</th><th className="text-left p-2">Операція</th><th className="text-left p-2">Око</th><th className="text-left p-2">Тип / Джерело</th><th className="text-left p-2">Статус</th><th className="text-left p-2">Дзвінок</th><th className="text-left p-2">Менеджер</th></tr>
                  </thead>
                  <tbody>
                    {recs.map((r) => {
                      const src = sources.find((s) => s.key === r.referral_source);
                      return (
                      <tr key={r.id} className="border-t border-slate-100">
                        <td className="p-2 whitespace-nowrap">{formatDateTime(r.created_at)}</td>
                        <td className="p-2">{r.operation_type}</td>
                        <td className="p-2">{r.eye}</td>
                        <td className="p-2 text-xs">
                          {r.patient_type === "primary" ? (
                            <Badge className="bg-sky-50 text-sky-700">Первинний</Badge>
                          ) : (
                            <div className="flex flex-col gap-0.5">
                              <Badge className="bg-violet-50 text-violet-700 w-fit">Направлений</Badge>
                              <span className="text-slate-500">{src?.label || r.referral_source || "—"}</span>
                              {r.external_doctor_name && <span className="text-slate-400 italic">{r.external_doctor_name}</span>}
                            </div>
                          )}
                        </td>
                        <td className="p-2"><Badge className={`${LIFECYCLE_BADGE[r.lifecycle_status] || "bg-slate-100 text-slate-700"} hover:${LIFECYCLE_BADGE[r.lifecycle_status]}`}>{r.lifecycle_label}</Badge></td>
                        <td className="p-2">{r.call_date || "—"}</td>
                        <td className="p-2">{r.manager_id || "—"}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* Quick Action: Create Recommendation */}
      <Dialog open={quickRecOpen} onOpenChange={setQuickRecOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Створити рекомендацію</DialogTitle>
            <DialogDescription>Пацієнт: <b>{patient.full_name}</b> (підставлено автоматично, змінити не можна).</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Пацієнт</Label>
              <Input disabled readOnly value={patient.full_name} className="bg-slate-50" />
            </div>
            <div className="md:col-span-2">
              <Label>Діагноз <span className="text-rose-500">*</span></Label>
              <textarea
                data-testid="qr-diagnosis"
                value={quickRec.diagnosis}
                onChange={(e) => setQuickRec({ ...quickRec, diagnosis: e.target.value })}
                placeholder="напр. Незріла катаракта OD, амбліопія"
                rows={3}
                className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0D9488]/30 resize-y"
              />
            </div>
            <div>
              <Label>Процедура <span className="text-rose-500">*</span></Label>
              <Select value={quickRec.procedure_id} onValueChange={(v) => setQuickRec({ ...quickRec, procedure_id: v })}>
                <SelectTrigger data-testid="qr-procedure"><SelectValue placeholder="Оберіть" /></SelectTrigger>
                <SelectContent className="max-h-80">
                  {procedures.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-xs text-slate-500 mt-1">Око визначається автоматично з назви процедури (OD / OS).</div>
            </div>
            <div>
              <Label>Тип пацієнта <span className="text-rose-500">*</span></Label>
              <Select value={quickRec.patient_type} onValueChange={(v) => setQuickRec({ ...quickRec, patient_type: v, referral_source: v === "primary" ? "" : quickRec.referral_source, external_doctor_id: v === "primary" ? "" : quickRec.external_doctor_id, business_trip_team: v === "primary" ? [] : quickRec.business_trip_team })}>
                <SelectTrigger data-testid="qr-patient-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="primary">Первинний</SelectItem>
                  <SelectItem value="referred">Направлений</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {quickIsReferred && (
              <div>
                <Label>Джерело направлення <span className="text-rose-500">*</span></Label>
                <Select value={quickRec.referral_source} onValueChange={(v) => setQuickRec({ ...quickRec, referral_source: v, external_doctor_id: "", business_trip_team: [] })}>
                  <SelectTrigger data-testid="qr-source"><SelectValue placeholder="Оберіть" /></SelectTrigger>
                  <SelectContent>
                    {sources.map((s) => (
                      <SelectItem key={s.key} value={s.key}>
                        {s.label}
                        {s.source_type === "external_doctor" ? " · зовн. лікар" : ""}
                        {s.requires_team ? " · потребує бригаду" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {quickIsReferred && quickSrcIsExtDoctor && (
              <div className="md:col-span-2">
                <Label>Зовнішній лікар <span className="text-rose-500">*</span></Label>
                <Select value={quickRec.external_doctor_id} onValueChange={(v) => setQuickRec({ ...quickRec, external_doctor_id: v })}>
                  <SelectTrigger data-testid="qr-ext-doctor"><SelectValue placeholder="Оберіть" /></SelectTrigger>
                  <SelectContent>
                    {extDoctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.full_name}{d.clinic ? ` · ${d.clinic}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Відповідальний менеджер</Label>
              <Select value={quickRec.manager_id || "none"} onValueChange={(v) => setQuickRec({ ...quickRec, manager_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Не призначено —</SelectItem>
                  {employees.filter((e) => e.role === "manager").map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {quickIsReferred && quickSrcNeedsTeam && (
              <div className="md:col-span-2 p-3 border-2 border-dashed border-[#0D9488] rounded-md bg-teal-50/30" data-testid="qr-trip-team-block">
                <Label className="text-sm font-semibold text-[#0F2027]">Бригада направлення (обов&apos;язково)</Label>
                <QuickTeamEditor team={quickRec.business_trip_team} employees={employees.filter((e) => (e.groups || ["clinic"]).includes("trip"))}
                  onChange={(team) => setQuickRec({ ...quickRec, business_trip_team: team })} />
              </div>
            )}
            <div className="md:col-span-2">
              <Label>Коментар</Label>
              <Input value={quickRec.comment} onChange={(e) => setQuickRec({ ...quickRec, comment: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setQuickRecOpen(false)}>Скасувати</Button>
            <Button onClick={submitQuickRec} className="bg-[#0F2027]" data-testid="qr-save"><Save className="h-4 w-4 mr-1" />Створити</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Action: Schedule Call */}
      <Dialog open={quickCallOpen} onOpenChange={setQuickCallOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Запланувати дзвінок</DialogTitle>
            <DialogDescription>
              Створить подію в часовій стрічці пацієнта{recs.some((r) => !r.closed && r.status === "active") ? " та оновить дату дзвінка в активній рекомендації." : "."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Дата дзвінка <span className="text-rose-500">*</span></Label>
              <Input type="date" data-testid="qc-date" value={quickCall.call_date} onChange={(e) => setQuickCall({ ...quickCall, call_date: e.target.value })} />
            </div>
            <div>
              <Label>Відповідальний менеджер</Label>
              <Select value={quickCall.manager_id || "none"} onValueChange={(v) => setQuickCall({ ...quickCall, manager_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="qc-manager"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Не призначено —</SelectItem>
                  {employees.filter((e) => e.role === "manager").map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Коментар</Label>
              <Input data-testid="qc-comment" value={quickCall.comment} onChange={(e) => setQuickCall({ ...quickCall, comment: e.target.value })} placeholder="Що обговорити, нагадати тощо" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setQuickCallOpen(false)}>Скасувати</Button>
            <Button onClick={submitQuickCall} className="bg-[#0D9488]" data-testid="qc-save"><Save className="h-4 w-4 mr-1" />Зберегти</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Action: Add Comment */}
      <Dialog open={quickNoteOpen} onOpenChange={setQuickNoteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Додати коментар</DialogTitle>
            <DialogDescription>Коментар з&apos;явиться у вкладці «Події».</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Текст коментаря <span className="text-rose-500">*</span></Label>
            <Input data-testid="qn-text" value={quickNote.description} onChange={(e) => setQuickNote({ description: e.target.value })} autoFocus />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setQuickNoteOpen(false)}>Скасувати</Button>
            <Button onClick={submitQuickNote} className="bg-[#0F2027]" data-testid="qn-save"><Save className="h-4 w-4 mr-1" />Зберегти</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={eventDlg} onOpenChange={setEventDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Додати подію</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Тип</Label>
              <Select value={eventForm.event_type} onValueChange={(v) => setEventForm({ ...eventForm, event_type: v })}>
                <SelectTrigger data-testid="event-type"><SelectValue /></SelectTrigger>
                <SelectContent>{MANUAL_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Заголовок</Label>
              <Input data-testid="event-title" value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} placeholder="Авто з типу, якщо порожнє" />
            </div>
            <div>
              <Label>Опис</Label>
              <Input data-testid="event-desc" value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEventDlg(false)}><X className="h-4 w-4 mr-1" />Скасувати</Button>
            <Button onClick={addEvent} className="bg-[#0D9488]" data-testid="event-save"><Save className="h-4 w-4 mr-1" />Зберегти</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const TRIP_ROLES = [
  { value: "doctor", label: "Лікар" },
  { value: "optometrist", label: "Оптометрист" },
  { value: "manager", label: "Менеджер" },
  { value: "salesperson", label: "Фахівець оптики" },
  { value: "paramedic", label: "Фельдшер" },
  { value: "registrar", label: "Реєстратор" },
];

function QuickTeamEditor({ team, employees, onChange }) {
  const add = () => onChange([...(team || []), { employee_id: "", role: "doctor", share_percent: 100 }]);
  const rm = (idx) => onChange(team.filter((_, i) => i !== idx));
  const upd = (idx, key, val) => {
    const next = [...team]; next[idx] = { ...next[idx], [key]: val }; onChange(next);
  };
  return (
    <div className="space-y-2 mt-2">
      {(team || []).map((p, idx) => {
        const opts = employees.filter((e) => e.role === p.role || p.role === "salesperson");
        return (
          <div key={idx} className="grid grid-cols-[1fr_2fr_80px_36px] gap-2 items-end p-2 bg-white border border-slate-200 rounded-md">
            <Select value={p.role} onValueChange={(v) => upd(idx, "role", v)}>
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>{TRIP_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={p.employee_id} onValueChange={(v) => upd(idx, "employee_id", v)}>
              <SelectTrigger className="h-8"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{opts.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" className="h-8" value={p.share_percent} onChange={(e) => upd(idx, "share_percent", parseFloat(e.target.value || 0))} />
            <Button size="icon" variant="ghost" onClick={() => rm(idx)}><X className="h-4 w-4 text-red-500" /></Button>
          </div>
        );
      })}
      <Button size="sm" variant="outline" onClick={add} data-testid="qr-add-trip-member"><Plus className="h-3 w-3 mr-1" />Додати учасника</Button>
    </div>
  );
}

